import type { AxiosResponse } from "axios";

import { buildQueryParams } from "../utils/buildQueryParams";
import { toReportQueryFilters } from "../utils/report/reportRows.util";
import { APIInstance } from "./api.config";
import type {
  AccountReportQuery,
  AccountReportResponse,
  SubcontractorReportQuery,
  SubcontractorReportResponse,
} from "./analytics.types";

const { api } = new APIInstance({ path: "/v1/analytics" });

export const AnalyticsService = {
  getWorkerAnalytics: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/workers/transactions?${params.toString()}`);
  },

  getWorkerLedger: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/workers/ledger?${params.toString()}`);
  },

  getSubcontractorProjectSummary: (subcontractorId) => {
    return api.get(`/vendors/subcontractor/${subcontractorId}/project-summary`);
  },

  getSubcontractorAnalytics: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/vendors/subcontractor/transactions?${params.toString()}`);
  },

  getSubcontractorLedger: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/vendors/subcontractor/ledger?${params.toString()}`);
  },

  getAccountLedger: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/accounts/ledger?${params.toString()}`);
  },

  getVendorAnalytics: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/vendors/vendor/transactions?${params.toString()}`);
  },

  getVendorLedger: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/vendors/vendor/ledger?${params.toString()}`);
  },

  getClientAnalytics: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/clients/transactions?${params.toString()}`);
  },

  getClientLedger: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/clients/ledger?${params.toString()}`);
  },

  getCompanyBalanceSummary: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/companies/balance-summary?${params.toString()}`);
  },

  getCompanyProjectSummary: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/companies/project-summary?${params.toString()}`);
  },

  // Company-wide P&L rollup: totals, profitable/loss/break-even counts, and the full
  // per-project list ordered by profit desc. One call replaces the project-summary sweep.
  getPnlSummary: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/companies/pnl-summary?${params.toString()}`);
  },

  // Zero-filled income/expense time series (granularity: "day" | "month").
  getCompanyIncomeExpenseSeries: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/companies/income-expense-series?${params.toString()}`);
  },

  // Projects past their end date and not DONE/CANCELLED, most-delayed first.
  getDelayedProjects: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/projects/delayed?${params.toString()}`);
  },

  // Task counts by status (+ overdue) for the company, optionally scoped to one project.
  getCompanyTaskSummary: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/projects/task-summary?${params.toString()}`);
  },
  getLeadStatusSummary: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/companies/lead-status-summary?${params.toString()}`);
  },

  getProjectStageSummary: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/companies/project-stage-summary?${params.toString()}`);
  },

  getDailyReport: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/companies/daily-report?${params.toString()}`);
  },

  downloadDailyReportPdf: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/companies/daily-report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getUserAnalytics: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/users/activities?${params.toString()}`);
  },

  getCategorySummary: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/expenses/category-summary?${params.toString()}`);
  },

  getExpenseReport: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/expenses/report?${params.toString()}`);
  },

  downloadExpenseReportPdf: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/expenses/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getProjectReport: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/projects/report?${params.toString()}`);
  },

  downloadProjectReportPdf: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/projects/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getVendorReport: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/vendors/vendor/report?${params.toString()}`);
  },

  downloadVendorReportPdf: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/vendors/vendor/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getWorkerReport: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/workers/report?${params.toString()}`);
  },

  downloadWorkerReportPdf: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/workers/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getUserReport: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/users/report?${params.toString()}`);
  },

  downloadUserReportPdf: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/users/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getClientReport: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/clients/report?${params.toString()}`);
  },

  downloadClientReportPdf: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/clients/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getMaterialReport: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/materials/report?${params.toString()}`);
  },

  downloadMaterialReportPdf: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/materials/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getAccountReport: (query: AccountReportQuery): Promise<AxiosResponse<AccountReportResponse>> => {
    const params = buildQueryParams(toReportQueryFilters({ ...query }));
    return api.get(`/accounts/report?${params.toString()}`);
  },

  downloadAccountReportPdf: (query: AccountReportQuery): Promise<AxiosResponse<Blob>> => {
    const params = buildQueryParams(toReportQueryFilters({ ...query }));
    return api.get(`/accounts/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },

  getSubcontractorReport: (query: SubcontractorReportQuery): Promise<AxiosResponse<SubcontractorReportResponse>> => {
    const params = buildQueryParams(toReportQueryFilters({ ...query }));
    return api.get(`/vendors/subcontractor/report?${params.toString()}`);
  },

  downloadSubcontractorReportPdf: (query: SubcontractorReportQuery): Promise<AxiosResponse<Blob>> => {
    const params = buildQueryParams(toReportQueryFilters({ ...query }));
    return api.get(`/vendors/subcontractor/report/download-pdf?${params.toString()}`, { responseType: "blob" });
  },
};
